import PageBackTitle from "../../Component/PageBackTitle";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { HiOutlinePlus } from "react-icons/hi";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { TbRefresh } from "react-icons/tb";
import { IoSearchOutline } from "react-icons/io5";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import useClickOutside from "../../useClickOutside";
import "../../tables/User.css";

function TrainingStandardsListPage({ isSidebarExpanded }) {
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState({});
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  useClickOutside([containerRef, buttonRef], () => {
    setIsSortPopup(false);
  });

  const showSnackbar = useCallback((message, nextSeverity = "success") => {
    setSnackbarMessage(message);
    setSeverity(nextSeverity);
    setSnackbarOpen(true);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await hrRecordsApi.listTrainingStandards({
        include_inactive: true,
      });
      setStandards(response || []);
    } catch (error) {
      console.error("Error fetching training standards:", error);
      showSnackbar("Failed to load training standards", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const visibleStandards = standards.filter((standard) =>
    [standard.display_name, standard.standard_body, standard.ref_no_label, standard.section_label]
      .filter(Boolean)
      .some((value) =>
        String(value).toLowerCase().includes(searchInput.trim().toLowerCase()),
      ),
  );

  const handleSort = (column) => {
    const order =
      sortColumn === column && sortOrder[column] === "asc" ? "desc" : "asc";

    setSortColumn(column);
    setSortOrder((prevOrder) => ({ ...prevOrder, [column]: order }));
    setIsSortPopup(false);

    const sorted = [...standards].sort((a, b) => {
      const aValue = a[column]?.toString().toLowerCase() || "";
      const bValue = b[column]?.toString().toLowerCase() || "";
      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
    setStandards(sorted);
  };

  const handleDeactivate = async (standard) => {
    const nextValue = !standard.is_active;
    try {
      await hrRecordsApi.updateTrainingStandard(standard.id, {
        is_active: nextValue,
      });
      showSnackbar(
        `Training standard ${nextValue ? "activated" : "deactivated"} successfully`,
      );
      fetchData();
    } catch (error) {
      console.error("Error updating standard:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to update training standard",
        "error",
      );
    }
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content" ref={containerRef}>
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Regulatory Standards</PageBackTitle>
              <div className="data-list-heading-content-right-side">
                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    className="search-bar"
                  />
                  <IoSearchOutline className="search-bar-icon" />
                </div>

                <Link to="/add-hr-training-standard" className="data-list-new">
                  <Tooltip title="New Training Standard" arrow>
                    <button className="new-button">
                      <HiOutlinePlus className="new-plus-icon" /> New
                    </button>
                  </Tooltip>
                </Link>

                <Tooltip title="Refresh List" arrow>
                  <button className="refresh-button" onClick={fetchData}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>

                <button
                  className="sort-button"
                  onClick={() => setIsSortPopup(!isSortPopup)}
                  ref={buttonRef}
                >
                  <PiDotsThreeOutlineVerticalFill className="sort-icon" />
                </button>
                {isSortPopup && (
                  <div className="sort-popup-container" ref={containerRef}>
                    <div className="sort-popup-content">
                      <div className="sort-by">
                        SORT BY{" "}
                        {sortOrder[sortColumn] === "asc"
                          ? "(Ascending)"
                          : "(Descending)"}
                      </div>
                      <div className="sort-by-button">
                        <button onClick={() => handleSort("display_name")}>
                          Display Name{" "}
                          {sortOrder.display_name === "asc" ? (
                            <MdExpandMore />
                          ) : (
                            <MdExpandLess />
                          )}
                        </button>
                        <br />
                        <button onClick={() => handleSort("standard_body")}>
                          Standard Body{" "}
                          {sortOrder.standard_body === "asc" ? (
                            <MdExpandMore />
                          ) : (
                            <MdExpandLess />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="data-list-body" onClick={() => setIsSortPopup(false)}>
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : visibleStandards.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. No.</th>
                        <th>DISPLAY NAME</th>
                        <th>STANDARD BODY</th>
                        <th>REF LABEL</th>
                        <th>SECTION LABEL</th>
                        <th>ACTIVE</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {visibleStandards.map((standard, index) => (
                        <tr key={standard.id} className="hoverable-row">
                          <td className="center-alignment">{index + 1}</td>
                          <td>{standard.display_name || "NA"}</td>
                          <td>{standard.standard_body || "NA"}</td>
                          <td>{standard.ref_no_label || "NA"}</td>
                          <td>{standard.section_label || "NA"}</td>
                          <td>{standard.is_active ? "Yes" : "No"}</td>
                          <td className="center-alignment">
                            <div className="hr-records-inline-actions">
                              <Link to={`/hr-training-standard-view/${standard.id}`}>
                                <button className="button-bg">View</button>
                              </Link>
                              <Link to={`/edit-hr-training-standard/${standard.id}`}>
                                <button className="button-bg">Edit</button>
                              </Link>
                              <button
                                className="button-bg"
                                onClick={() => handleDeactivate(standard)}
                              >
                                {standard.is_active ? "Deactivate" : "Activate"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">
                      No Training Standards Found
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default TrainingStandardsListPage;
